import { cache } from 'react';
import type { CatalogProviderSummary } from './catalog.types';
import {
  CatalogApiError,
  fetchCatalogProviders,
  normalizeCatalogSearchFilters,
  type CatalogSearchFilters,
  type NormalizedCatalogSearchFilters
} from './catalog.api';

export type CatalogProviderLoaderResult = {
  providers: CatalogProviderSummary[];
  pagination: {
    total: number;
    totalItems: number;
    page: number;
    limit: number;
    totalPages: number;
  };
  filters: NormalizedCatalogSearchFilters;
  degraded: boolean;
};

export const loadCatalogProviders = cache(
  async (
    filters?: CatalogSearchFilters
  ): Promise<CatalogProviderLoaderResult> => {
    const normalized = normalizeCatalogSearchFilters(filters);
    try {
      const payload = await fetchCatalogProviders(normalized);
      return {
        providers: payload.items,
        pagination: {
          total: payload.total,
          totalItems: payload.totalItems,
          page: payload.page,
          limit: payload.limit,
          totalPages: payload.totalPages
        },
        filters: normalized,
        degraded: false
      } satisfies CatalogProviderLoaderResult;
    } catch (error) {
      const isCatalogError = error instanceof CatalogApiError;
      // Any failure surfacing as CatalogApiError is, by definition, an
      // infrastructure-level problem talking to the catalog API: 4xx schema
      // rejects, 5xx upstream outages, dropped sockets that surface as
      // status:200 with an unreadable body, or DB statement timeouts that
      // truncate the response mid-stream. None of these should crash the
      // public Server Components tree — degrade to an empty result so the
      // page still renders. Non-CatalogApiError errors (programmer bugs)
      // still bubble up.
      if (isCatalogError) {
        // eslint-disable-next-line no-console
        console.error('[loadCatalogProviders] degrading to empty result', {
          status: error.status,
          message: error.message
        });
        return makeEmptyCatalogResult(normalized);
      }
      throw error;
    }
  }
);

const makeEmptyCatalogResult = (
  normalized: NormalizedCatalogSearchFilters
): CatalogProviderLoaderResult => ({
  providers: [],
  pagination: {
    total: 0,
    totalItems: 0,
    page: normalized.page,
    limit: normalized.limit,
    totalPages: 0
  },
  filters: normalized,
  degraded: true
});
